import { useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DocumentPicker } from './DocumentPicker.js';

interface MetadataRow {
  readonly metadataId: string;
  readonly metaKey: string;
  readonly metaValue: string | null;
  readonly dataType: string;
  readonly isMandatory: boolean;
}

export function M23MetadataManagementPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [documentId, setDocumentId] = useState('');
  const [rows, setRows] = useState<MetadataRow[] | null>(null);
  const [metaKey, setMetaKey] = useState('');
  const [metaValue, setMetaValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  function load(id: string): void {
    setError(null);
    apiRequest<{ data: MetadataRow[] }>(`/v1/documents/${encodeURIComponent(id)}/metadata`, { token })
      .then((r) => setRows(r.data))
      .catch((e) => setError((e as Error).message));
  }

  function onLoad(e: FormEvent): void {
    e.preventDefault();
    const id = documentId.trim();
    if (!id) return;
    load(id);
  }

  function onSet(e: FormEvent): void {
    e.preventDefault();
    const id = documentId.trim();
    if (!id) return;
    setMessage(null);
    setError(null);
    apiRequest(`/v1/documents/${encodeURIComponent(id)}/metadata`, {
      method: 'PUT',
      token,
      body: { metaKey, metaValue },
    })
      .then(() => { setMessage(t('m23.metadata.msgSaved')); setMetaKey(''); setMetaValue(''); load(id); })
      .catch((err) => setError((err as Error).message));
  }

  return (
    <main aria-label="metadata-management">
      <h1>{t('m23.metadata.title')}</h1>
      <form aria-label="metadata-load-form" onSubmit={onLoad}>
        <DocumentPicker label={t('m23.metadata.documentId')} value={documentId} onPick={(id) => setDocumentId(id)} />
        <button type="submit">{t('m23.metadata.loadMetadata')}</button>
      </form>
      <form aria-label="metadata-set-form" onSubmit={onSet}>
        <label htmlFor="metaKey">{t('m23.metadata.attributeKey')}</label>
        <input id="metaKey" value={metaKey} onChange={(e) => setMetaKey(e.target.value)} />
        <label htmlFor="metaValue">{t('m23.metadata.attributeValue')}</label>
        <input id="metaValue" value={metaValue} onChange={(e) => setMetaValue(e.target.value)} />
        <button type="submit">{t('m23.metadata.saveAttribute')}</button>
      </form>
      {message && <p role="status">{message}</p>}
      {error && <p role="alert">{error}</p>}
      {rows && (
        <table aria-label="metadata-table">
          <thead><tr><th>{t('m23.metadata.colKey')}</th><th>{t('m23.metadata.colValue')}</th><th>{t('m23.metadata.colType')}</th><th>{t('m23.metadata.colMandatory')}</th></tr></thead>
          <tbody>
            {rows.map((m) => (
              <tr key={m.metadataId}>
                <td>{m.metaKey}</td>
                <td>{m.metaValue ?? '—'}</td>
                <td>{m.dataType}</td>
                <td>{m.isMandatory ? t('m23.metadata.yes') : t('m23.metadata.no')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </main>
  );
}
